import * as yup from 'yup';
import moment from 'moment';

import { OUTPUT_CUSTOMER_DATE_FORMAT } from 'components/modules/Insurance/Insurance.constants';
import {
  CUSTOMER_BIRTHDATE_SCHEMA,
  CUSTOMER_DRIVING_EXPERIENCE_SCHEMA,
  CUSTOMER_DRIVING_LICENSE_ISSUE_DATE_SCHEMA,
  CUSTOMER_DRIVING_LICENSE_SERIES_AND_NUMBER_SCHEMA,
  CUSTOMER_FULL_NAME_SCHEMA,
} from 'components/modules/Insurance/Insurance.validation';
import { FIELDS } from 'store/insuranceAnketa/constants';

import { VALIDATION_TYPES, VOID_VALIDATION } from '../../Insurance.constants';

const DRIVING_LICENSE_SCHEMA_MAP = {
  [FIELDS.DRIVING_EXPERIENCE]: CUSTOMER_DRIVING_EXPERIENCE_SCHEMA.clone(),
  [FIELDS.DRIVING_LICENSE_ISSUE_DATE]: CUSTOMER_DRIVING_LICENSE_ISSUE_DATE_SCHEMA.clone().when(
    FIELDS.DRIVING_LICENSE_IS_FIRST,
    {
      is: false,
      then: yup
        .mixed()
        .test(
          VALIDATION_TYPES.INCORRECT,
          'Не может быть раньше, чем "Дата выдачи предыдущего ВУ"',
          function (value) {
            const { [FIELDS.PREVIOUS_DRIVING_LICENSE_ISSUE_DATE]: prevIssueDate } = this.parent;

            const lastValidDate = moment(prevIssueDate, OUTPUT_CUSTOMER_DATE_FORMAT);
            const currentLicenseDate = moment(value, OUTPUT_CUSTOMER_DATE_FORMAT);

            const diff = lastValidDate.diff(currentLicenseDate);

            return diff <= 0;
          }
        ),
      otherwise: VOID_VALIDATION,
    }
  ),
  [FIELDS.DRIVING_LICENSE_SERIES_AND_NUMBER]:
    CUSTOMER_DRIVING_LICENSE_SERIES_AND_NUMBER_SCHEMA.clone(),

  [FIELDS.PREVIOUS_DRIVING_LICENSE_ISSUE_DATE]: yup.mixed().when(FIELDS.DRIVING_LICENSE_IS_FIRST, {
    is: false,
    then: CUSTOMER_DRIVING_LICENSE_ISSUE_DATE_SCHEMA.clone().test(
      VALIDATION_TYPES.INCORRECT,
      'Не может быть позже, чем "Дата выдачи"',
      function (value) {
        const { [FIELDS.DRIVING_LICENSE_ISSUE_DATE]: licenseDate } = this.parent;

        const lastValidDate = moment(licenseDate, OUTPUT_CUSTOMER_DATE_FORMAT);
        const currentLicenseDate = moment(value, OUTPUT_CUSTOMER_DATE_FORMAT);

        const diff = lastValidDate.diff(currentLicenseDate);

        return diff >= 0;
      }
    ),
    otherwise: VOID_VALIDATION,
  }),
  [FIELDS.PREVIOUS_DRIVING_LICENSE_SERIES_AND_NUMBER]:
    CUSTOMER_DRIVING_LICENSE_SERIES_AND_NUMBER_SCHEMA.clone(),
};

const PASSPORT_ADDRESS_SCHEMA = yup
  .mixed()
  .test(
    VALIDATION_TYPES.REQUIRED,
    ({ path }) => {
      return path === FIELDS.LIVING_ADDRESS
        ? 'Введите адрес проживания'
        : 'Введите адрес регистрации';
    },
    Boolean
  )
  .test(
    VALIDATION_TYPES.REQUIRED,
    'Выберите значение из выпадающего списка или введите данные вручную',
    function () {
      const {
        [FIELDS.LIVING_ADDRESS_DA_DATA_INFO]: living,
        [FIELDS.REGISTRATION_ADDRESS_DA_DATA_INFO]: registration,
      } = this.parent;

      return this.path === FIELDS.LIVING_ADDRESS
        ? typeof living !== 'undefined'
        : typeof registration !== 'undefined';
    }
  );
const PASSPORT_ISSUE_DATE_SCHEMA = yup
  .mixed()
  .test(VALIDATION_TYPES.REQUIRED, 'Введите дату выдачи', Boolean)
  .test(VALIDATION_TYPES.INCORRECT, 'Не ранее 14 лет', function (value) {
    const { [FIELDS.BIRTHDATE]: birthdate } = this.parent;

    const lastValidDate = moment(birthdate, OUTPUT_CUSTOMER_DATE_FORMAT).add(14, 'years');

    const date = moment(value, OUTPUT_CUSTOMER_DATE_FORMAT);
    const diff = lastValidDate.diff(date);

    return diff <= 0;
  })
  .test(VALIDATION_TYPES.INCORRECT, 'Паспорт подлежит замене', function (value) {
    const { [FIELDS.BIRTHDATE]: birthdate } = this.parent;

    const now = moment().subtract(90, 'days');
    const bd = moment(birthdate, OUTPUT_CUSTOMER_DATE_FORMAT);
    const id = moment(value, OUTPUT_CUSTOMER_DATE_FORMAT);

    const age = now.diff(bd, 'years', true);
    const passportAge = id.diff(bd, 'years', true);

    if (age >= 20 && passportAge < 20) {
      return false;
    }

    return !(age >= 45 && passportAge < 45);
  });
const PASSPORT_SCHEMA_MAP = {
  [FIELDS.BIRTH_PLACE]: yup.string().required('Введите место рождения по паспорту'),

  [FIELDS.LIVING_ADDRESS]: yup.mixed().when(FIELDS.LIVING_ADDRESS_EQUALS, {
    is: false,
    then: PASSPORT_ADDRESS_SCHEMA.clone(),
    otherwise: VOID_VALIDATION,
  }),

  [FIELDS.PASSPORT_ISSUE_DATE]: PASSPORT_ISSUE_DATE_SCHEMA.clone(),
  [FIELDS.PASSPORT_ISSUER]: yup.string().required('Введите кем выдан паспорт'),
  [FIELDS.PASSPORT_ISSUER_CODE]: yup
    .string()
    .required('Введите код подразделения')
    .matches(/\d{3}-\d{3}/, 'Некорректный формат кода'),
  [FIELDS.PASSPORT_SERIES_AND_NUMBER]: yup
    .string()
    .required('Введите серию и номер')
    .matches(/\d{10}/, 'Некорректная серия или номер'),

  [FIELDS.PREVIOUS_PASSPORT_ISSUE_DATE]: yup.mixed().when(FIELDS.PASSPORT_CHANGED, {
    is: true,
    then: PASSPORT_ISSUE_DATE_SCHEMA.clone(),
    otherwise: VOID_VALIDATION,
  }),
  [FIELDS.PREVIOUS_PASSPORT_NUMBER]: yup.mixed().when(FIELDS.PASSPORT_CHANGED, {
    is: true,
    then: yup
      .mixed()
      .test(VALIDATION_TYPES.REQUIRED, 'Введите номер', Boolean)
      .test(VALIDATION_TYPES.INCORRECT, 'Некорректный номер', (value) => {
        return /\d{6}/.test(value ?? '');
      }),
    otherwise: VOID_VALIDATION,
  }),
  [FIELDS.PREVIOUS_PASSPORT_SERIES]: yup.mixed().when(FIELDS.PASSPORT_CHANGED, {
    is: true,
    then: yup
      .mixed()
      .test(VALIDATION_TYPES.REQUIRED, 'Введите серию', Boolean)
      .test(VALIDATION_TYPES.INCORRECT, 'Некорректная серия', (value) => {
        return /\d{4}/.test(value ?? '');
      }),
    otherwise: VOID_VALIDATION,
  }),

  [FIELDS.REGISTRATION_ADDRESS]: PASSPORT_ADDRESS_SCHEMA.clone(),
};

const PERSONAL_DATA_SCHEMA_MAP = {
  [FIELDS.BIRTHDATE]: CUSTOMER_BIRTHDATE_SCHEMA.clone(),
  [FIELDS.EMAIL]: yup.string().required('Введите почту').email('Некорректный адрес'),
  [FIELDS.FULL_NAME]: CUSTOMER_FULL_NAME_SCHEMA.clone(),
  [FIELDS.MOBILE_PHONE]: yup
    .string()
    .required('Введите мобильный телефон')
    .length(18, 'Введите мобильный телефон'),
};

const BASE_SCHEMA_MAP = { ...PASSPORT_SCHEMA_MAP, ...PERSONAL_DATA_SCHEMA_MAP };

const cloneSchemaMap = ({ map, transform }) => {
  return Object.entries(map).reduce((acc, [path, schema]) => {
    const clone = schema.clone();
    const result = transform?.({ path, schema: clone }) ?? clone;

    return { ...acc, [path]: result };
  }, {});
};

export const policyholderSchema = yup.object().shape({
  ...cloneSchemaMap({ map: BASE_SCHEMA_MAP }),
  ...cloneSchemaMap({
    map: DRIVING_LICENSE_SCHEMA_MAP,
    transform: ({ schema }) => {
      return yup.mixed().when(FIELDS.OWNER_IS_DRIVER, {
        is: true,
        then: schema,
        otherwise: VOID_VALIDATION,
      });
    },
  }),
  [FIELDS.KASKO_INSURER]: yup
    .mixed()
    .when([FIELDS.KASKO_INSURER_IS_DRIVER, FIELDS.OWNER_IS_INSURER], {
      is: (isDriver, isOwner) => {
        return !isDriver && !isOwner;
      },
      then: yup.object().shape(cloneSchemaMap({ map: BASE_SCHEMA_MAP })),
      otherwise: yup.object(),
    })
    .when([FIELDS.KASKO_INSURER_IS_DRIVER, FIELDS.OWNER_IS_INSURER], {
      is: (isDriver, isOwner) => {
        return isDriver && !isOwner;
      },
      then: yup.object().shape({
        ...cloneSchemaMap({ map: BASE_SCHEMA_MAP }),
        ...cloneSchemaMap({ map: DRIVING_LICENSE_SCHEMA_MAP }),
      }),
      otherwise: yup.object(),
    }),
});
